import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";

// Kalem bazlı üretim aşaması etiketleri (küçük statik map — kanban meta'sıyla uyumlu)
const STAGE_TR = {
  teklif: "Teklif", siparis_onay: "Sipariş Onay", is_emri: "İş Emri", bom: "Ürün Reçetesi",
  satinalma: "Satınalma", malzeme_kabul: "Malzeme Kabul", kumas_kontrol: "Kumaş Kontrol",
  pastal: "Pastal", kesim: "Kesim", baski: "Baskı", nakis: "Nakış", dikim: "Dikim",
  son_kalite: "Son Kalite", paketleme: "Paketleme", koli_hazirlik: "Koli Hazırlık",
  irsaliye_fatura: "İrsaliye / Fatura", sevkiyat: "Sevkiyat", tamamlandi: "Tamamlandı",
  iptal: "İptal",
};
import { formatVariantLabel } from "@/lib/variants";
import { useAuth } from "@/context/AuthContext";
import { ProductionFormDialog } from "@/components/ProductionFormDialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Loader2, Printer, Package, Palette, Warehouse, User, Calendar,
  Flame, Layers, X,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");

const POS_LABEL = {
  gogus_sol: "Göğüs Sol", gogus_sag: "Göğüs Sağ", gogus_orta: "Göğüs Orta",
  sirt: "Sırt", sol_kol: "Sol Kol", sag_kol: "Sağ Kol",
  yaka_arka: "Yaka Arkası", sapka_on: "Şapka Ön", sapka_yan: "Şapka Yan",
  sapka_arka: "Şapka Arka", on_etek: "Ön Etek", arka_etek: "Arka Etek",
  gogus: "Göğüs",
};
const TECH_LABEL = {
  baski: "Baskı", nakis: "Nakış", transfer: "Transfer", arma: "Arma",
  dtf: "DTF", sublime: "Sublimasyon", flock: "Flok", flok: "Flok",
};

const PRIORITY_META = {
  dusuk: { label: "Düşük", cls: "text-slate-500 border-slate-500/40 bg-slate-500/10" },
  normal: { label: "Normal", cls: "text-blue-500 border-blue-500/40 bg-blue-500/10" },
  yuksek: { label: "Yüksek", cls: "text-blue-500 border-blue-500/40 bg-blue-500/10" },
  acil: { label: "ACİL", cls: "text-rose-500 border-rose-500/40 bg-rose-500/10" },
};

export default function OrderDetailDialog({ order, open, onOpenChange }) {
  const [fullOrder, setFullOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState({}); // artwork_id -> blob url
  const [prodFormOpen, setProdFormOpen] = useState(false);
  const { can } = useAuth();
  // P1 · orders.view_commercial — Fiyat/KDV/toplam/iskonto/maliyet
  // görüntüleme yetkisi. Backend her istekte maskeleme yapar; UI de
  // defense-in-depth ile bu kolonları / total blokunu gizler.
  const hasCommercial = can("orders", "view_commercial");

  useEffect(() => {
    if (!open || !order?.id) return;
    setLoading(true);
    setFullOrder(null);
    (async () => {
      try {
        const { data } = await api.get(`/orders/${order.id}`);
        setFullOrder(data);
        // Preload logo images
        for (const lp of (data.logo_placements || [])) {
          if (!lp.artwork_id || previews[lp.artwork_id]) continue;
          try {
            const r = await api.get(`/artwork/download/${lp.artwork_id}`, { responseType: "blob" });
            setPreviews((prev) => ({ ...prev, [lp.artwork_id]: URL.createObjectURL(r.data) }));
          } catch { /* ignore */ }
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [open, order?.id]);

  const printPdf = async () => {
    if (!order?.id) return;
    try {
      const res = await api.get(`/orders/${order.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      // Open in new window and trigger print
      const win = window.open(url, "_blank");
      if (win) {
        win.addEventListener("load", () => { setTimeout(() => win.print(), 400); });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printProductionPdf = () => { if (order?.id) setProdFormOpen(true); };

  const o = fullOrder || order || {};
  const pr = PRIORITY_META[o.priority] || PRIORITY_META.normal;
  // Support both new (line_items) and legacy (flat product_*) structures
  let items = o.line_items || o.items || [];
  if (items.length === 0 && (o.product_id || o.product_name)) {
    // Reconstruct a single item from flat fields for legacy orders
    items = [{
      product_id: o.product_id || "",
      product_name: o.product_name || "—",
      variant_id: o.variant_id || "",
      variant_sku: o.variant_sku || "",
      variant_attributes: o.variant_attributes || {},
      warehouse_name: o.warehouse_name || "",
      quantity: o.quantity || 0,
      unit_price: o.unit_price || 0,
      currency: "TRY",
      tax_rate: 20,
      discount_percent: 0,
      discount_amount: 0,
      line_total: o.total_price || (o.quantity * o.unit_price) || 0,
      size_breakdown: o.size_breakdown || {},
      _legacy: true,
    }];
  }
  const logos = o.logo_placements || [];

  // FAZ 5 M6 — Per-currency totals (teklif aşamasında çevrim yok)
  const currencyOrder = { TRY: 0, USD: 1, EUR: 2, GBP: 3 };
  const currencySymbolMap = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
  const currencySymbolOf = (c) => currencySymbolMap[(c || "TRY").toUpperCase()] || (c || "");
  const perCurrency = {};
  for (const it of items) {
    const cur = (it.currency || "TRY").toUpperCase();
    const v = Number(it.line_total || it.total_price || 0);
    perCurrency[cur] = (perCurrency[cur] || 0) + v;
  }
  const currencies = Object.keys(perCurrency).sort(
    (a, b) => (currencyOrder[a] ?? 99) - (currencyOrder[b] ?? 99) || a.localeCompare(b),
  );
  const isMixedCurrency = currencies.length > 1;
  const grandTotal = items.reduce((s, it) => s + Number(it.line_total || it.total_price || 0), 0);
  const grandQty = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
  const hasSizeBreakdown = items.some((it) => it.size_breakdown && Object.keys(it.size_breakdown || {}).length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-5xl" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-primary">{o.order_no || "—"}</span>
            <span className="text-muted-foreground text-sm font-normal">·</span>
            <span>Sipariş Detayı</span>
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-mono font-bold border rounded-sm py-1 px-2 ${pr.cls}`}>
              {o.priority === "acil" && <Flame className="w-3 h-3" />}
              {pr.label}
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="order-detail-body">
            {/* Meta */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetaCard icon={User} label="Müşteri" value={o.customer_name || "—"} />
              <MetaCard icon={Calendar} label="Termin" value={fmtDate(o.due_date)} />
              <MetaCard icon={Layers} label="Aşama" value={o.stage || "—"} />
              <MetaCard icon={Package} label="Toplam Adet" value={grandQty} highlight />
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs uppercase font-mono text-muted-foreground">
                  Sipariş Kalemleri ({items.length})
                </div>
                {items.some((it) => it._legacy) && (
                  <div className="text-[10px] text-amber-500 font-mono uppercase tracking-wider">
                    Eski sipariş (tek ürün)
                  </div>
                )}
              </div>
              <div className="border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm" data-testid="order-detail-items">
                  <thead className="bg-muted/40 border-b border-border">
                    <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                      <th className="text-left px-3 py-2">Ürün</th>
                      <th className="text-left px-3 py-2">Varyant / Beden</th>
                      <th className="text-left px-3 py-2">Depo</th>
                      <th className="text-right px-3 py-2">Adet</th>
                      {hasCommercial && <th className="text-right px-3 py-2">Birim</th>}
                      {hasCommercial && <th className="text-right px-3 py-2">İskonto</th>}
                      {hasCommercial && <th className="text-right px-3 py-2">KDV</th>}
                      {hasCommercial && <th className="text-right px-3 py-2">Toplam</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.length === 0 ? (
                      <tr><td colSpan={hasCommercial ? 8 : 4} className="px-3 py-4 text-center text-muted-foreground text-xs italic">
                        Kalem verisi yok
                      </td></tr>
                    ) : (
                      items.map((it, i) => {
                        const varLabel = formatVariantLabel(it.variant_attributes, it.variant_sku || "—");
                        return (
                          <tr key={i}>
                            <td className="px-3 py-2 font-semibold">
                              {it.product_name || "—"}
                              {/* Kalem bazlı üretim aşaması (fallback: order.stage) */}
                              {(it.production_stage || it.production_started) && (
                                <div className="text-[10px] font-mono uppercase tracking-wider text-blue-500 dark:text-blue-400" data-testid={`order-detail-line-stage-${i}`}>
                                  {(STAGE_TR[it.production_stage] || it.production_stage || "")}
                                  {it.production_started ? "" : " · başlamadı"}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs font-semibold text-emerald-500 dark:text-emerald-400">{varLabel}</td>
                            <td className="px-3 py-2 text-xs">{it.warehouse_name || "—"}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold">{it.quantity}</td>
                            {hasCommercial && (
                              <>
                                <td className="px-3 py-2 text-right font-mono" data-testid={`order-detail-unit-price-${i}`}>
                                  {currencySymbolOf(it.currency)}{fmt(it.unit_price)}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-rose-500">
                                  {it.discount_percent > 0 ? `%${it.discount_percent}` : it.discount_amount > 0 ? fmt(it.discount_amount) : "—"}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-muted-foreground">%{it.tax_rate ?? 0}</td>
                                <td className="px-3 py-2 text-right font-mono font-bold text-primary" data-testid={`order-detail-line-total-${i}`}>
                                  {currencySymbolOf(it.currency)}{fmt(it.line_total || it.total_price || 0)}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {hasCommercial && items.length > 0 && (
                    <tfoot className="bg-muted/40 border-t border-border">
                      {isMixedCurrency && (
                        <tr>
                          <td colSpan={8} className="px-3 py-1.5 text-right text-[10px] uppercase font-mono text-amber-500" data-testid="order-detail-mixed-badge">
                            Karışık Para Birimi · {currencies.join(" + ")} · Teklif toplamı çevrilmez
                          </td>
                        </tr>
                      )}
                      {currencies.map((cur) => (
                        <tr key={cur} className="text-sm font-bold" data-testid={`order-detail-total-${cur}`}>
                          <td colSpan={7} className="px-3 py-2 text-right uppercase text-xs font-mono">
                            Genel Toplam{isMixedCurrency ? ` (${cur})` : ""}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-primary">
                            {currencySymbolOf(cur)}{fmt(perCurrency[cur])}
                          </td>
                        </tr>
                      ))}
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* Sevkiyat & Faturalama durumu (kalem bazlı) */}
            {(() => {
              const isLegacyLine = items.some((it) => it._legacy);
              const shipInfo = items.map((it, i) => {
                const shipped = isLegacyLine
                  ? Number(o.shipped_total || 0)
                  : Number(it.shipped_qty || 0);
                const invoiced = isLegacyLine
                  ? Number(o.invoiced_total || 0)
                  : Number(it.invoiced_qty || 0);
                return { i, name: it.product_name, shipped, invoiced, remaining: Math.max(0, shipped - invoiced) };
              });
              const anyShipped = shipInfo.some((x) => x.shipped > 0 || x.invoiced > 0);
              if (!anyShipped) return null;
              const tShipped = shipInfo.reduce((s, x) => s + x.shipped, 0);
              const tInvoiced = shipInfo.reduce((s, x) => s + x.invoiced, 0);
              const tRemaining = Math.max(0, tShipped - tInvoiced);
              return (
                <div data-testid="order-detail-ship-invoice">
                  <div className="text-xs uppercase font-mono text-muted-foreground mb-1.5">
                    Sevkiyat & Faturalama
                  </div>
                  <div className="border border-border rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 border-b border-border">
                        <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                          <th className="text-left px-3 py-1.5">Ürün</th>
                          <th className="text-right px-3 py-1.5">Sevk Edilen</th>
                          <th className="text-right px-3 py-1.5">Faturalanan</th>
                          <th className="text-right px-3 py-1.5">Faturalanabilir Kalan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {shipInfo.map((x) => (
                          <tr key={x.i} data-testid={`order-detail-shipline-${x.i}`}>
                            <td className="px-3 py-1.5 font-semibold">{x.name || "—"}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{x.shipped}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">{x.invoiced}</td>
                            <td className={`px-3 py-1.5 text-right font-mono font-bold ${x.remaining > 0 ? "text-amber-500" : "text-emerald-500"}`}
                              data-testid={`order-detail-remaining-${x.i}`}>{x.remaining}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-muted/40 border-t border-border">
                        <tr className="text-sm font-bold" data-testid="order-detail-ship-total">
                          <td className="px-3 py-1.5 text-right uppercase text-xs font-mono">Toplam</td>
                          <td className="px-3 py-1.5 text-right font-mono">{tShipped}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{tInvoiced}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-primary">{tRemaining}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Size breakdown (production hint) */}
            {hasSizeBreakdown && (
              <div>
                <div className="text-xs uppercase font-mono text-muted-foreground mb-1.5">
                  Beden Dağılımı (Üretim İçin)
                </div>
                <div className="grid grid-cols-1 gap-2" data-testid="order-detail-sizes">
                  {items.map((it, i) => {
                    const breakdown = it.size_breakdown || {};
                    if (Object.keys(breakdown).length === 0) return null;
                    const entries = Object.entries(breakdown).filter(([, q]) => Number(q) > 0);
                    if (entries.length === 0) return null;
                    return (
                      <div key={i} className="border border-border rounded-sm bg-muted/20 p-3">
                        <div className="text-xs font-semibold mb-2">{it.product_name}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {entries.map(([size, qty]) => (
                            <div
                              key={size}
                              className="bg-background border border-primary/40 rounded-sm px-2.5 py-1 text-xs"
                              data-testid={`order-size-${size}`}
                            >
                              <span className="uppercase font-mono text-[10px] text-muted-foreground">{size}</span>
                              <span className="ml-1.5 font-mono font-bold text-primary">×{qty}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Logo placements */}
            <div>
              <div className="text-xs uppercase font-mono text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5" /> Baskı / Logo ({logos.length})
              </div>
              {logos.length === 0 ? (
                <div className="border border-dashed border-border rounded-sm p-4 text-center text-xs text-muted-foreground italic">
                  Bu sipariş için baskı/logo tanımlanmamış.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="order-detail-logos">
                  {logos.map((lp, i) => (
                    <div key={i} className="border border-border rounded-sm bg-muted/30 p-2 flex gap-2 items-start">
                      <div className="w-14 h-14 bg-background rounded-sm flex items-center justify-center overflow-hidden shrink-0">
                        {previews[lp.artwork_id] ? (
                          <img src={previews[lp.artwork_id]} alt="" className="max-w-full max-h-full object-contain" />
                        ) : (
                          <Palette className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="text-sm font-semibold truncate">{lp.label || "Logo"}</div>
                        <div className="text-[10px] font-mono flex flex-wrap gap-x-2 gap-y-0.5">
                          <span className="text-primary">Konum: {POS_LABEL[lp.position] || lp.position || "—"}</span>
                          <span className="text-emerald-500">Teknik: {TECH_LABEL[lp.print_method] || lp.print_method || "—"}</span>
                        </div>
                        {lp.notes && <div className="text-[10px] text-muted-foreground italic truncate">{lp.notes}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            {o.notes && (
              <div>
                <div className="text-xs uppercase font-mono text-muted-foreground mb-1.5">Notlar</div>
                <div className="bg-muted/30 border border-border rounded-sm p-3 text-sm whitespace-pre-wrap">
                  {o.notes}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="order-detail-close">
            Kapat
          </Button>
          <Button
            data-testid="order-detail-production"
            onClick={printProductionPdf}
            disabled={!o.id}
            variant="outline"
            className="rounded-sm border-primary/40 text-primary hover:bg-primary/10"
            title="Üretim personeli için fiyat/KDV içermeyen teknik dokuman"
          >
            <Printer className="w-4 h-4 mr-1" /> Üretim Formu
          </Button>
          {hasCommercial && (
            <Button
              data-testid="order-detail-print"
              onClick={printPdf}
              disabled={!o.id}
              className="rounded-sm bg-primary hover:bg-primary/90"
            >
              <Printer className="w-4 h-4 mr-1" /> Sipariş Formu
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <ProductionFormDialog order={o} open={prodFormOpen} onClose={() => setProdFormOpen(false)} />
    </Dialog>
  );
}

function MetaCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-sm border p-2.5 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30"}`}>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider font-mono text-muted-foreground">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-sm font-bold truncate mt-0.5 ${highlight ? "text-primary font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}
